import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/api';
import { Callout } from '@/components/ui/Callout';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { PageSection } from '@/components/ui/PageSection';
import { StatCard } from '@/components/ui/StatCard';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import { useAppSelector } from '@/hooks/useAppSelector';
import { formatCurrency } from '@/utils/format';
import { showApiError } from '@/utils/apiError';

export function CompanyOverviewPanel() {
  const companyId = useAppSelector((s) => s.app.selectedCompany?.id);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['company-dashboard', companyId],
    queryFn: async () => {
      try {
        return await dashboardApi.companyStats();
      } catch (err) {
        showApiError(err, 'Failed to load company overview');
        throw err;
      }
    },
    enabled: !!companyId,
  });

  if (!companyId) {
    return (
      <Callout variant="info">
        Select a company in the header to see company-wide KPIs across HR, CRM, desk, procurement, and finance.
      </Callout>
    );
  }

  if (isLoading) {
    return <DashboardSkeleton kpiCount={8} />;
  }

  if (!stats) return null;

  return (
    <PageSection
      title={`${stats.companyName} (${stats.companyCode})`}
      description={`Company overview · ${stats.currencyCode}${stats.departmentRestricted ? ` · limited to ${stats.accessibleDepartmentCount} department(s)` : ''}`}
    >
      <KpiGrid columns={4}>
        <StatCard title="Stores" value={stats.storeCount} icon={<StorefrontIcon />} />
        <StatCard title="Warehouses" value={stats.warehouseCount} icon={<WarehouseIcon />} />
        <StatCard title="Headcount" value={stats.employeeHeadcount} icon={<GroupsIcon />} />
        <StatCard title="Pending leave" value={stats.pendingLeaveRequests} icon={<EventNoteIcon />} />
        <StatCard title="Open CRM deals" value={stats.openCrmDeals} icon={<HandshakeIcon />} />
        <StatCard title="Open CRM leads" value={stats.openCrmLeads} icon={<BusinessIcon />} />
        <StatCard title="Desk tickets" value={stats.openDeskTickets} icon={<SupportAgentIcon />} />
        <StatCard title="Open POs" value={stats.openPurchaseOrders} icon={<ShoppingCartIcon />} />
        <StatCard title="Finance accounts" value={stats.financeAccounts} icon={<AccountBalanceIcon />} />
        <StatCard
          title="Month store sales"
          value={formatCurrency(stats.monthStoreSales, stats.currencyCode)}
          icon={<TrendingUpIcon />}
        />
        <StatCard title="Month invoices" value={stats.monthStoreInvoices} icon={<TrendingUpIcon />} />
        <StatCard title="Payroll runs" value={stats.payrollRuns} icon={<GroupsIcon />} />
      </KpiGrid>
    </PageSection>
  );
}
